import { useRouteError } from "react-router";
import { useTranslation } from "react-i18next";

/**
 * BUG-085 (2026-06-04): app-wide route errorElement.
 *
 * React Router's DEFAULT error boundary renders a developer-facing English screen
 * ("Unexpected Application Error! … 🪐 Hey developer 👋 … provide a way better UX
 * with an ErrorBoundary or errorElement") whenever a lazy route chunk fails to
 * `import()` — the common trigger is the mobile candidate funnel dropping its
 * connection mid-flow, or a stale deploy swapping chunk hashes mid-session.
 *
 * This replaces that with a friendly localized connection-problem screen + reload
 * action (ES/EN/DE via the `errors` namespace). Wired as the `errorElement` on the
 * pathless root route in App.tsx so it covers every surface (candidate trees +
 * protected admin tree). The reload button uses explicit `var(--talenta-*)` (not
 * the `bg-primary` named token) so this safety surface always renders correctly,
 * independent of the shadcn token layer.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation("errors");

  // Diagnostics to the console only — never surface the raw error to the user.
  if (error) {
    // eslint-disable-next-line no-console
    console.error("Route error boundary caught:", error);
  }

  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="text-xl font-semibold">{t("route_boundary.title")}</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        {t("route_boundary.body")}
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="min-h-[44px] rounded-md px-5 text-base font-medium shadow-[var(--talenta-shadow-primary)] transition-opacity hover:opacity-90"
        style={{
          background: "var(--talenta-primary)",
          color: "var(--talenta-primary-foreground)",
        }}
      >
        {t("route_boundary.reload")}
      </button>
    </div>
  );
}
